import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { api } from 'shared/api/client';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { documentKeys } from 'entities/document';
import { i18n } from 'shared/i18n';
import { EventType, dispatchMessage } from 'shared/feedback/toasts';
import { styleKeys } from './keys';

const stripDefault = <T>(value: T): T => (typeof value === 'string' ? (value.replace(/\s*!default\s*$/, '') as T) : value);

const stripDefaults = <T extends object>(obj: T): T => Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, stripDefault(value)])) as T;

const DRAFT_CSS_TOAST_ID = 'draft-css-compile';

const draftCssErrorHandler = () => {
  dispatchMessage(EventType.ADD_MESSAGE, {
    id: DRAFT_CSS_TOAST_ID,
    type: 'warning',
    title: i18n('dc:preview.css-error-title'),
    text: i18n('dc:preview.css-error-message'),
    removeTime: 6000,
  });
};

export function useStyle({
  organization,
  documentRevisionId,
  styleId,
  parsed = true,
}: {
  organization?: unknown;
  documentRevisionId?: string;
  styleId?: string;
  parsed?: boolean;
}) {
  return useQuery({
    queryKey: styleKeys.style(organization, documentRevisionId, styleId),

    queryFn: async () => {
      const { data } = await api.GET('/styles/{id}', {
        params: { path: { id: styleId as string }, query: { parsed: String(parsed) as 'true' | 'false', documentRevisionId: String(documentRevisionId) } },
      });
      if (!data) return data;
      const stripped = stripDefaults(data);
      if (stripped.content && typeof stripped.content === 'object') {
        stripped.content = stripDefaults(stripped.content);
      }
      return stripped;
    },

    enabled: !!styleId && !!documentRevisionId,
    placeholderData: keepPreviousData,
  });
}

export function useStyles({
  organization,
  documentRevisionId,
  styleIds,
  parsed = true,
}: {
  organization?: unknown;
  documentRevisionId?: string;
  styleIds: (string | undefined)[];
  parsed?: boolean;
}) {
  return useQuery({
    queryKey: styleKeys.styles(organization, documentRevisionId, styleIds),

    queryFn: async () => {
      const { data } = await api.POST('/styles/batch', {
        body: { ids: styleIds.filter((id): id is string => !!id), documentRevisionId: String(documentRevisionId), parsed },
      });
      return data ?? [];
    },

    enabled: (Array.isArray(styleIds) ? styleIds.filter(Boolean).length > 0 : false) && !!documentRevisionId,
  });
}

export function useCss({
  documentSuperId,
  documentRevisionId,
  useDraft,
  withStyle,
  withTheme,
  baseline,
  embedd,
  keepPrevious = false,
}: {
  documentSuperId: number | string;
  documentRevisionId?: string;
  useDraft?: boolean;
  withStyle?: string | false;
  withTheme?: string;
  baseline?: string;
  embedd?: boolean;
  keepPrevious?: boolean;
}) {
  return useQuery({
    queryKey: styleKeys.css(documentSuperId, documentRevisionId, { useDraft, withStyle, withTheme, baseline, embedd }),

    queryFn: async () => {
      const { data } = await api.GET('/documents/{superId}/css', {
        params: {
          path: { superId: Number(documentSuperId) },
          query: {
            ...(useDraft ? { useDraft: 'true' } : {}),
            ...(withStyle ? { withStyle } : {}),
            ...(withTheme ? { withTheme } : {}),
            ...(baseline ? { baseline } : {}),
            ...(embedd ? { embedd: 'true' } : {}),
            ...(documentRevisionId ? { revisionId: documentRevisionId } : {}),
          },
        },
      });
      return data;
    },

    enabled: !!documentSuperId,

    placeholderData: keepPrevious ? keepPreviousData : undefined,

    meta: { errorHandler: useDraft ? draftCssErrorHandler : undefined },
  });
}

export function useSaveStyle({
  organization,
  documentSuperId,
  templateSuperId,
  pageId,
  documentRevisionId,
  styleIds,
}: {
  organization?: unknown;
  documentSuperId: number | string;
  templateSuperId?: string;
  pageId?: string;
  documentRevisionId?: string;
  styleIds?: string[];
}) {
  const queryClient = useQueryClient();
  const stylesQueryKey = styleKeys.styles(organization, documentRevisionId, styleIds || []);

  return useMutation({
    mutationFn: async (content: unknown) => {
      const styleId = uuidv4();

      const action = {
        action: 'update_page_style',
        data: {
          styleId,
          organization,
          documentSuperId,
          pageId,
          templateSuperId,
          content,
        },
      };
      const { data } = await api.POST('/documents/{superId}/actions', { params: { path: { superId: Number(documentSuperId) } }, body: action });
      return data;
    },

    onMutate: async (newContent: unknown) => {
      await queryClient.cancelQueries({
        queryKey: stylesQueryKey,
      });

      const previousStyles = queryClient.getQueryData<{ content?: unknown }[]>(stylesQueryKey);

      if (previousStyles && Array.isArray(previousStyles)) {
        const updatedStyles = previousStyles.map((style, index) => {
          if (style?.content && index === previousStyles.length - 1) {
            return { ...style, content: newContent };
          }
          return style;
        });
        queryClient.setQueryData(stylesQueryKey, updatedStyles);
      }

      return { previousStyles };
    },

    onError: (err: unknown, _newContent: unknown, context: { previousStyles?: { content?: unknown }[] } | undefined) => {
      if (context?.previousStyles) {
        queryClient.setQueryData(stylesQueryKey, context.previousStyles);
      }
      ServerErrorHandler(err);
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...styleKeys.cssAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...styleKeys.stylesAll],
      });
    },
  });
}

export function useSaveFontStyle({ documentSuperId, styleId }: { documentSuperId: number | string; styleId?: string }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (content: string) => {
      const action = {
        action: 'update_font_style',
        styleId,
        data: {
          content,
        },
      };
      const { data } = await api.POST('/documents/{superId}/actions', { params: { path: { superId: Number(documentSuperId) } }, body: action });
      return data;
    },

    onError: (error: unknown) => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'error',
        text: i18n('project-details:font-updated.success'),
        removeTime: 4000,
      });
      ServerErrorHandler(error);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...styleKeys.cssAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.detailAll],
      });
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'success',
        text: i18n('project-details:font-updated.success'),
        removeTime: 4000,
      });
    },
  });
}

export function useDeleteFontStyle({ documentSuperId, styleId }: { documentSuperId: number | string; styleId?: string }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const action = {
        action: 'delete_font_style',
        styleId,
      };
      const { data } = await api.POST('/documents/{superId}/actions', { params: { path: { superId: Number(documentSuperId) } }, body: action });
      return data;
    },

    onError: (error: unknown) => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'error',
        text: i18n('project-details:font-deleted-error'),
        removeTime: 4000,
      });
      ServerErrorHandler(error);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...styleKeys.cssAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.detailAll],
      });
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'success',
        text: i18n('project-details:font-deleted-success'),
        removeTime: 4000,
      });
    },
  });
}
